import { useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, arrayUnion, collection, deleteDoc, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from '../fs';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { usePanel } from '../App.jsx';
import { CLINICAS, nombreClinica } from '../clinicas';
import { rellenar } from '../respuestas.jsx';
import { validarCantidad, validarTelefono, validarTexto, primerError } from '../validar.js';

// Pruebas que se mandan a laboratorio externo y encargos de clientes: quién, qué, si ha llegado y si se ha avisado.
const ESTADOS = {
  prueba: [['enviada', 'Enviada'], ['recibido', 'Resultado recibido'], ['avisado', 'Dueño avisado']],
  encargo: [['pedido', 'Pedido'], ['llegado', 'Ha llegado'], ['avisado', 'Cliente avisado'], ['entregado', 'Entregado']],
};
const FINAL = { prueba: 'avisado', encargo: 'entregado' };
const ACCION = { // estado actual → [botón, siguiente estado]
  prueba: { enviada: ['Ha llegado el resultado', 'recibido'], recibido: ['Avisar al dueño', 'avisado'] },
  encargo: { pedido: ['Ha llegado', 'llegado'], llegado: ['Avisar al cliente', 'avisado'], avisado: ['Entregado', 'entregado'] },
};
const COLOR = { enviada: '#64748b', pedido: '#64748b', recibido: '#b45309', llegado: '#b45309', avisado: '#15803d', entregado: '#15803d' };
const DIAS_AVISO = { prueba: 5, encargo: 7 }; // a partir de aquí sale como "va con retraso"
const LABS = ['Idexx', 'Laboratorio Dr. Barba', 'Echevarne', 'Histovet', 'Garmogén', 'Retamas', 'Xalab', 'Uranovet'];

const hoy = () => new Date();
const dias = (ts) => (ts?.seconds ? Math.floor((hoy() - ts.seconds * 1000) / 864e5) : 0);
const fCorta = (ts) => (ts?.seconds ? new Date(ts.seconds * 1000).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : '');
const tel = (t) => { const d = String(t || '').replace(/\D/g, ''); return d.length === 9 ? `34${d}` : d; };

export const necesitaAccion = (p) => p.abierto && (['recibido', 'llegado'].includes(p.estado) || (p.estado === (p.tipo === 'prueba' ? 'enviada' : 'pedido') && dias(p.creado) >= DIAS_AVISO[p.tipo]));

// Para el contador del menú
export function usePendientes(clinicas) {
  const [l, setL] = useState([]);
  useEffect(() => {
    if (!clinicas.length) return undefined;
    return onSnapshot(query(collection(db, 'pendientes'), where('clinica', 'in', clinicas), where('abierto', '==', true)),
      (s) => setL(s.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setL([]));
  }, [clinicas.join(',')]);
  return l;
}

const mensaje = (p) => rellenar(p.tipo === 'prueba'
  ? `{saludo}{nombre}, le escribimos de {clinica}. Ya tenemos los resultados de ${p.que || 'la prueba'} de ${p.mascota || 'su mascota'}. En breve le llamará el veterinario para comentárselos; si lo prefiere, puede llamarnos al {telefono}. Un saludo.`
  : `{saludo}{nombre}, le escribimos de {clinica}. Ya ha llegado su pedido (${p.que}${p.cantidad > 1 ? ` x${p.cantidad}` : ''}). Puede pasar a recogerlo cuando quiera en nuestro horario. Un saludo.`,
{ nombre: p.cliente, clinica: p.clinica });

export default function Pendientes() {
  const { user, clinica, clinicasActivas } = usePanel();
  const [tipo, setTipo] = useState('prueba');
  const [vista, setVista] = useState('abiertos');
  const [abiertos, setAbiertos] = useState([]);
  const [cerrados, setCerrados] = useState([]);
  const [sinAsignar, setSinAsignar] = useState([]);
  const [nuevo, setNuevo] = useState(false);
  const [permiso, setPermiso] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'denied');
  const previos = useRef(null);
  const yo = user?.displayName || user?.email || '';

  useEffect(() => onSnapshot(query(collection(db, 'pendientes'), where('clinica', 'in', clinicasActivas), where('abierto', '==', true)), (s) => {
    const l = s.docs.map((d) => ({ id: d.id, ...d.data() }));
    // Aviso del navegador cuando algo pasa a "ha llegado"
    if (previos.current) for (const p of l) {
      const antes = previos.current[p.id];
      if (['recibido', 'llegado'].includes(p.estado) && antes && antes !== p.estado && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification(p.tipo === 'prueba' ? `Resultado de ${p.mascota || 'una prueba'}` : `Ha llegado el encargo de ${p.cliente || 'un cliente'}`, { body: `${p.que || ''} · ${nombreClinica(p.clinica)}. Falta avisar.` });
      }
    }
    previos.current = Object.fromEntries(l.map((p) => [p.id, p.estado]));
    setAbiertos(l);
  }, () => setAbiertos([])), [clinicasActivas.join(',')]);
  useEffect(() => vista !== 'cerrados' ? undefined : onSnapshot(query(collection(db, 'pendientes'), where('clinica', 'in', clinicasActivas), where('abierto', '==', false)),
    (s) => setCerrados(s.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (b.actualizado?.seconds || 0) - (a.actualizado?.seconds || 0)).slice(0, 80)), () => setCerrados([])), [vista, clinicasActivas.join(',')]);
  useEffect(() => onSnapshot(query(collection(db, 'resultadosLlegados'), where('clinica', 'in', clinicasActivas), where('asignado', '==', false)),
    (s) => setSinAsignar(s.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setSinAsignar([])), [clinicasActivas.join(',')]);

  const lista = useMemo(() => (vista === 'cerrados' ? cerrados : abiertos).filter((p) => p.tipo === tipo)
    .sort((a, b) => Number(necesitaAccion(b)) - Number(necesitaAccion(a)) || (a.creado?.seconds || 0) - (b.creado?.seconds || 0)), [abiertos, cerrados, vista, tipo]);
  const cuenta = (t) => abiertos.filter((p) => p.tipo === t && necesitaAccion(p)).length;

  const avanzar = async (p, estado) => {
    // Resultados: si la clínica tiene la plantilla activada, el WhatsApp sale solo (la función marca la prueba como avisada)
    if (estado === 'avisado' && p.tipo === 'prueba' && p.telefono) {
      try { const { data } = await httpsCallable(functions, 'avisarResultadosWa')({ id: p.id }); if (data?.enviado) return; }
      catch (e) { if (!window.confirm(`No se pudo enviar automáticamente (${e.message}). ¿Abrir WhatsApp para mandarlo a mano?`)) return; }
    }
    if (estado === 'avisado' && p.telefono) window.open(`https://wa.me/${tel(p.telefono)}?text=${encodeURIComponent(mensaje(p))}`, '_blank');
    await updateDoc(doc(db, 'pendientes', p.id), {
      estado, abierto: estado !== FINAL[p.tipo], actualizado: serverTimestamp(),
      historial: arrayUnion({ estado, por: yo, fecha: new Date().toISOString() }),
    });
  };
  const deshacer = (p) => {
    const orden = ESTADOS[p.tipo].map((e) => e[0]);
    const i = orden.indexOf(p.estado);
    if (i > 0) updateDoc(doc(db, 'pendientes', p.id), { estado: orden[i - 1], abierto: true, actualizado: serverTimestamp() });
  };
  const asignar = async (r, p) => {
    await updateDoc(doc(db, 'pendientes', p.id), { estado: 'recibido', actualizado: serverTimestamp(), resultado: { asunto: r.asunto, enlace: r.enlace, fecha: r.fecha }, historial: arrayUnion({ estado: 'recibido', por: 'correo', fecha: new Date().toISOString() }) });
    await updateDoc(doc(db, 'resultadosLlegados', r.id), { asignado: true });
  };

  return (
    <section className="pendientes">
      <div className="cabecera">
        <div><h1>Pruebas y encargos</h1><p className="subtitulo">{nombreClinica(clinica)} · muestras a laboratorio y pedidos de clientes</p></div>
        <div className="acciones">
          {permiso === 'default' && <button className="btn sec mini" onClick={async () => setPermiso(await Notification.requestPermission())}>Activar avisos</button>}
          <button className="btn" onClick={() => setNuevo(true)}>+ {tipo === 'prueba' ? 'Nueva prueba' : 'Nuevo encargo'}</button>
        </div>
      </div>

      <div className="barra-filtros">
        <div className="filtros">
          <button className={tipo === 'prueba' ? 'on' : ''} onClick={() => setTipo('prueba')}>Pruebas{cuenta('prueba') > 0 && <span className="pend-badge">{cuenta('prueba')}</span>}</button>
          <button className={tipo === 'encargo' ? 'on' : ''} onClick={() => setTipo('encargo')}>Encargos{cuenta('encargo') > 0 && <span className="pend-badge">{cuenta('encargo')}</span>}</button>
        </div>
        <button className="enlace" onClick={() => setVista(vista === 'abiertos' ? 'cerrados' : 'abiertos')}>{vista === 'abiertos' ? 'Ver terminados' : '← En marcha'}</button>
      </div>

      {nuevo && <Nuevo tipo={tipo} clinicas={clinicasActivas} yo={yo} onCerrar={() => setNuevo(false)} />}

      {tipo === 'prueba' && sinAsignar.length > 0 && vista === 'abiertos' && (
        <div className="tarjeta pend-sin">
          <b>Resultados en el correo sin asignar ({sinAsignar.length})</b>
          {sinAsignar.map((r) => (
            <div key={r.id} className="pend-sin-fila">
              <div><a href={r.enlace} target="_blank" rel="noreferrer">{r.asunto}</a><div className="tenue" style={{ fontSize: '.78rem' }}>{r.de} · {r.fecha}{clinicasActivas.length > 1 ? ` · ${nombreClinica(r.clinica)}` : ''}</div></div>
              <select defaultValue="" onChange={(e) => { const p = abiertos.find((x) => x.id === e.target.value); if (p) asignar(r, p); }}>
                <option value="" disabled>Asignar a…</option>
                {abiertos.filter((p) => p.tipo === 'prueba' && p.estado === 'enviada' && p.clinica === r.clinica).map((p) => <option key={p.id} value={p.id}>{p.mascota} · {p.que}</option>)}
              </select>
              <button className="btn sec mini" onClick={() => updateDoc(doc(db, 'resultadosLlegados', r.id), { asignado: true })}>No es de una prueba</button>
            </div>
          ))}
        </div>
      )}

      <div className="lista">
        {lista.length === 0 ? <p className="lista-vacia">{vista === 'cerrados' ? 'Nada terminado todavía.' : tipo === 'prueba' ? 'No hay pruebas pendientes.' : 'No hay encargos pendientes.'}</p> : (
          <div className="pend-lista">
            {lista.map((p) => {
              const acc = ACCION[p.tipo][p.estado];
              const urge = necesitaAccion(p);
              const est = ESTADOS[p.tipo].find((e) => e[0] === p.estado)?.[1];
              return (
                <div key={p.id} className={`pend-fila ${urge ? 'urge' : ''}`}>
                  <div className="pend-estado"><span className="estado-txt" style={{ color: COLOR[p.estado] }}>● {est}</span>
                    <small className="tenue">{fCorta(p.creado)} · {dias(p.creado) === 0 ? 'hoy' : `hace ${dias(p.creado)} d`}</small></div>
                  <div className="pend-que">
                    <b>{p.tipo === 'prueba' ? `${p.mascota || '—'}` : p.cliente || '—'}</b>
                    {p.tipo === 'prueba' && p.cliente && <span className="tenue"> · {p.cliente}</span>}
                    {p.tipo === 'encargo' && p.mascota && <span className="tenue"> · {p.mascota}</span>}
                    <div>{p.que}{p.cantidad > 1 ? ` x${p.cantidad}` : ''}{p.donde && <span className="tenue"> · {p.donde}</span>}</div>
                    <div className="tenue" style={{ fontSize: '.78rem' }}>
                      {p.tipo === 'prueba' ? 'Enviada' : 'Pedido'} por {p.por || '—'}{clinicasActivas.length > 1 ? ` · ${nombreClinica(p.clinica)}` : ''}{p.telefono ? ` · ${p.telefono}` : ''}
                      {p.pagado && ' · pagado'}{p.notas && ` · ${p.notas}`}
                    </div>
                    {p.resultado?.enlace && <div style={{ fontSize: '.8rem' }}>Resultado: <a href={p.resultado.enlace} target="_blank" rel="noreferrer">{p.resultado.asunto || 'Ver resultado'}</a></div>}
                    {urge && ['enviada', 'pedido'].includes(p.estado) && <div className="tarde" style={{ fontSize: '.8rem' }}>{dias(p.creado)} días sin llegar · reclamar al {p.tipo === 'prueba' ? 'laboratorio' : 'proveedor'}</div>}
                  </div>
                  <div className="pend-acc">
                    {acc && <button className={`btn mini ${urge ? '' : 'sec'}`} onClick={() => avanzar(p, acc[1])} title={acc[1] === 'avisado' && p.telefono ? 'Abre WhatsApp con el mensaje escrito' : ''}>{acc[0]}</button>}
                    {acc?.[1] === 'avisado' && <button className="btn sec mini" onClick={() => { navigator.clipboard?.writeText(mensaje(p)).catch(() => {}); }}>Copiar mensaje</button>}
                    {acc?.[1] === 'avisado' && <button className="btn sec mini" onClick={() => updateDoc(doc(db, 'pendientes', p.id), { estado: 'avisado', abierto: p.tipo !== 'prueba', actualizado: serverTimestamp(), historial: arrayUnion({ estado: 'avisado', por: yo, fecha: new Date().toISOString(), como: 'llamada' }) })}>Avisado por teléfono</button>}
                    <span className="pend-mini">
                      {p.estado !== ESTADOS[p.tipo][0][0] && <button onClick={() => deshacer(p)} title="Volver al paso anterior">↶</button>}
                      <button onClick={() => { if (window.confirm('¿Borrar?')) deleteDoc(doc(db, 'pendientes', p.id)); }} title="Borrar">✕</button>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function Nuevo({ tipo, clinicas, yo, onCerrar }) {
  const [f, setF] = useState({ clinica: clinicas[0], mascota: '', cliente: '', telefono: '', que: '', donde: '', cantidad: 1, por: yo, pagado: false, notas: '' });
  const [guardando, setGuardando] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  // AUD-PEN-01/03: teléfono válido, cantidad entera 1–999, textos acotados
  const vCant = validarCantidad(f.cantidad, { etiqueta: 'La cantidad', max: 999, obligatorio: tipo !== 'prueba' });
  const err = primerError(validarTelefono(f.telefono), tipo === 'prueba' ? null : vCant, validarTexto(f.que, { etiqueta: 'La descripción', max: 200 }), validarTexto(f.notas, { etiqueta: 'Las notas', max: 500 }),
    validarTexto(f.mascota, { etiqueta: 'La mascota', max: 120 }), validarTexto(f.cliente, { etiqueta: 'El cliente', max: 120 }), validarTexto(f.donde, { etiqueta: 'El proveedor o laboratorio', max: 80 }), validarTexto(f.por, { etiqueta: 'Quién lo apunta', max: 80 }));
  const ok = f.que.trim() && (tipo === 'prueba' ? f.mascota.trim() : f.cliente.trim()) && !err;
  const guardar = async (e) => {
    e.preventDefault(); if (!ok) return;
    setGuardando(true);
    const estado = ESTADOS[tipo][0][0];
    await addDoc(collection(db, 'pendientes'), {
      ...f, telefono: f.telefono.trim(), notas: f.notas.trim(), que: f.que.trim(), cantidad: tipo === 'prueba' ? 1 : vCant.valor || 1, tipo, estado, abierto: true, creado: serverTimestamp(), actualizado: serverTimestamp(),
      historial: [{ estado, por: f.por, fecha: new Date().toISOString() }],
    });
    onCerrar();
  };
  return (
    <form className="tarjeta pend-form" onSubmit={guardar}>
      <b>{tipo === 'prueba' ? 'Nueva prueba a laboratorio' : 'Nuevo encargo'}</b>
      <div className="pend-campos">
        {clinicas.length > 1 && <select value={f.clinica} onChange={set('clinica')}>{CLINICAS.filter((c) => clinicas.includes(c.id)).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}</select>}
        {tipo === 'prueba'
          ? <>
            <input className="campo" placeholder="Mascota *" value={f.mascota} onChange={set('mascota')} autoFocus />
            <input className="campo" placeholder="Dueño" value={f.cliente} onChange={set('cliente')} />
            <input className="campo" placeholder="Teléfono (para avisar)" value={f.telefono} onChange={set('telefono')} inputMode="tel" type="tel" maxLength={20} />
            <input className="campo grande" placeholder="Qué prueba *" value={f.que} onChange={set('que')} />
            <input className="campo" list="labs" placeholder="Laboratorio" value={f.donde} onChange={set('donde')} />
            <datalist id="labs">{LABS.map((l) => <option key={l} value={l} />)}</datalist>
          </>
          : <>
            <input className="campo" placeholder="Cliente *" value={f.cliente} onChange={set('cliente')} autoFocus />
            <input className="campo" placeholder="Teléfono (para avisar)" value={f.telefono} onChange={set('telefono')} inputMode="tel" type="tel" maxLength={20} />
            <input className="campo" placeholder="Mascota" value={f.mascota} onChange={set('mascota')} />
            <input className="campo grande" placeholder="Qué ha pedido *" value={f.que} onChange={set('que')} />
            <label className="tenue">Cantidad <input className="campo num" style={{ width: 60 }} type="number" min="1" max="999" step="1" value={f.cantidad} onChange={set('cantidad')} /></label>
            <input className="campo" placeholder="Proveedor" value={f.donde} onChange={set('donde')} />
            <label className="tenue"><input type="checkbox" checked={f.pagado} onChange={set('pagado')} /> Ya pagado</label>
          </>}
        <label className="tenue">{tipo === 'prueba' ? 'Enviada por' : 'Lo apunta'} <input className="campo" value={f.por} onChange={set('por')} /></label>
        <input className="campo grande" placeholder="Notas" maxLength={500} value={f.notas} onChange={set('notas')} />
      </div>
      {err && (f.telefono || f.notas || f.que || String(f.cantidad) !== '1') && <p className="error" role="alert" style={{ margin: '.3rem 0' }}>{err}</p>}
      <div className="acciones">
        <button className="btn" disabled={!ok || guardando}>Guardar</button>
        <button type="button" className="btn sec" onClick={onCerrar}>Cancelar</button>
      </div>
    </form>
  );
}
